import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import LoadingButton from "_shared/_views/loading/loading-button";
import { useId, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";

/**
 * One "add something to this collection" affordance — "Add Member", "Add Existing Category",
 * "From Marketplace Team", "Upload Policy Document".
 *
 * `disabled` offers the affordance greyed out rather than withheld, for the cases where it is the
 * viewer's but cannot be taken right now: an add whose caller has no setter to hand the selection to,
 * or a document upload, which the server refuses until the proposal itself has been saved. `loading`
 * is for that same upload while it is in flight, and `icon` for an add whose mark is not the plus — a
 * marketplace import and a template copy each carry their own.
 */
export type TableAddAction = {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  loading?: boolean;
  disabled?: boolean;
  /**
   * Why a disabled alternative cannot be taken yet, written under its label in the dropdown. A
   * disabled menu item takes no pointer events, so a tooltip on it would never open.
   */
  disabledReason?: string;
};

/**
 * The accessible name of the split button's dropdown, and the label a test or a screen reader reads
 * before the alternatives open. Exported because the browser harness opens the menu by this name.
 */
export const MORE_WAYS_TO_ADD_LABEL = "More ways to add";

/** The same empty list on every render, for a collection that offers nothing to add. */
const NO_ADD_ACTIONS: readonly TableAddAction[] = [];

/**
 * How a toolbar addresses this control in its own styles, without reaching for an Emotion class name
 * or for whichever MUI root a one-way or many-way add happens to render. `CollectionToolbar` uses it
 * to decide which piece of a wrapping row gives way first.
 */
export const TABLE_ADD_ACTIONS_ATTRIBUTE = "data-table-add-actions";

/**
 * Also on the add control's element when standalone actions are drawn beside it. That element is wider,
 * so `CollectionToolbar` moves it below the search at a wider toolbar than it moves a lone add control.
 */
export const TABLE_ADD_ACTIONS_WITH_STANDALONE_ATTRIBUTE = "data-table-add-actions-with-standalone";

/**
 * Keeps the group from being stretched by a wrapping toolbar row and from giving way to the search
 * field beside it: the labels are already as short as they read.
 */
const AddActionsGroup = styled(ButtonGroup)(() => ({
  flexShrink: 0,
  width: "fit-content",
}));

const AddActionsSingle = styled(LoadingButton)(() => ({
  flexShrink: 0,
  width: "fit-content",
}));

/** What a disabled single add control's tooltip listens to, in the button's place in the toolbar. */
const DisabledSingleAnchor = styled("span")(() => ({
  display: "inline-flex",
  flexShrink: 0,
  width: "fit-content",
}));

/**
 * The add control and the standalone actions after it, as one flex item of the toolbar row. One item,
 * so a narrow toolbar moves them down together rather than splitting them across two lines (#5808).
 * They wrap inside it only where the pane is narrower than the two side by side.
 */
const AddActionsRow = styled("div")(({ theme }) => ({
  // Stretched, so a standalone action is as tall as the split button beside it: the split button's
  // ▾ is a 24px icon and makes the group a pixel taller than a small text button (#6041).
  alignItems: "stretch",
  display: "flex",
  flexShrink: 0,
  flexWrap: "wrap",
  gap: theme.spacing(1),
  maxWidth: "100%",
}));

/**
 * A standalone action's button. The wrapper carries the tooltip, because a disabled button takes no
 * pointer events and could not open one itself.
 */
const StandaloneActionWrapper = styled("span")(() => ({
  display: "inline-flex",
  flexShrink: 0,
  width: "fit-content",
}));

export type TableAddActionsProps = {
  /**
   * The ways this collection may be added to, in the order they are read. The FIRST is the split
   * button's own press; the rest are the alternatives behind its dropdown.
   */
  actions?: readonly TableAddAction[];
  /**
   * Actions on the collection that are not ways of adding to it, each drawn as its own button after the
   * add control rather than inside its dropdown — a proposal tab's Save as Template (#5808). A disabled
   * one stays in place and gives its `disabledReason` in a tooltip.
   */
  standaloneActions?: readonly TableAddAction[];
};

/**
 * A collection's add affordances, as one control however many there are.
 *
 * One way to add is one ordinary button, exactly as it has always been drawn. More than one is MUI's
 * split button — the first action on the button itself, the rest on a dropdown beside it — rather than
 * a row of equal buttons, which is what the Fee Schedule and Vendor Team tabs had grown and what made
 * their toolbars wider than the panes they sit in (#4955).
 *
 * The dropdown IS a modal surface, and that is the point of it: it opens because the reader pressed
 * it, the same way MRT's own column-actions menus do, and it gives the page back on dismissal. What
 * #4895 forbade — and what the roster and collection specs still guard — is a surface that opens with
 * nobody having asked, which this never does.
 */
export default function TableAddActions(props: Readonly<TableAddActionsProps>) {
  const actions = props.actions ?? NO_ADD_ACTIONS;
  const standaloneActions = props.standaloneActions ?? NO_ADD_ACTIONS;

  if (standaloneActions.length === 0) return <AddControl actions={actions} marked />;

  return (
    <AddActionsRow data-table-add-actions="" data-table-add-actions-with-standalone="">
      <AddControl actions={actions} marked={false} />
      {standaloneActions.map((action) => (
        <StandaloneAction key={action.label} action={action} />
      ))}
    </AddActionsRow>
  );
}

/**
 * The add control itself. `marked` puts the toolbar's attribute on it, for when nothing is drawn beside
 * it; otherwise the row around it carries the attribute.
 */
function AddControl(props: Readonly<{ actions: readonly TableAddAction[]; marked: boolean }>) {
  const { actions, marked } = props;
  const attribute = marked ? { [TABLE_ADD_ACTIONS_ATTRIBUTE]: "" } : {};
  // The toggle itself is the anchor, held in state rather than in a ref: a ref read during the render
  // to position a portal is what `react-hooks/refs` reports, and an eslint-disable for any
  // `react-hooks/*` rule costs the whole component its React Compiler memoization (see app/CLAUDE.md).
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchor);
  const menuId = useId();

  const primary = actions[0];
  const alternatives = actions.slice(1);

  if (!primary) return null;

  if (alternatives.length === 0) {
    // A disabled button takes no pointer events, so a reason for it is given on a span around it,
    // which also carries the toolbar's attribute in the button's place (#6595).
    const reason = primary.disabled ? primary.disabledReason : undefined;
    if (reason) {
      return (
        <Tooltip title={reason}>
          <DisabledSingleAnchor {...attribute}>
            <AddActionsSingle
              startIcon={primary.icon ?? <AddIcon />}
              variant="outlined"
              size="small"
              loading={primary.loading ?? false}
              disabled
              onClick={primary.onClick}
            >
              {primary.label}
            </AddActionsSingle>
          </DisabledSingleAnchor>
        </Tooltip>
      );
    }
    return (
      <AddActionsSingle
        {...attribute}
        startIcon={primary.icon ?? <AddIcon />}
        variant="outlined"
        size="small"
        loading={primary.loading ?? false}
        disabled={primary.disabled ?? false}
        onClick={primary.onClick}
      >
        {primary.label}
      </AddActionsSingle>
    );
  }

  return (
    <>
      <AddActionsGroup {...attribute} variant="outlined" size="small">
        <LoadingButton
          startIcon={primary.icon ?? <AddIcon />}
          loading={primary.loading ?? false}
          disabled={primary.disabled ?? false}
          onClick={primary.onClick}
        >
          {primary.label}
        </LoadingButton>
        <Button
          aria-label={MORE_WAYS_TO_ADD_LABEL}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          size="small"
          onClick={(event: ReactMouseEvent<HTMLButtonElement>) => setMenuAnchor(event.currentTarget)}
        >
          <ArrowDropDownIcon />
        </Button>
      </AddActionsGroup>
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {alternatives.map((action, index) => {
          const reason = action.disabled ? action.disabledReason : undefined;
          const reasonId = `${menuId}-reason-${index}`;
          return (
            <MenuItem
              key={action.label}
              // Named by its label alone, with the reason it is disabled as its description, so a reader
              // hears "Add Existing Category, dimmed" and then why rather than one long run-on name.
              aria-label={action.label}
              aria-describedby={reason ? reasonId : undefined}
              disabled={action.disabled ?? false}
              onClick={() => {
                setMenuAnchor(null);
                action.onClick();
              }}
            >
              {/* The mark an alternative carries is its own — a folder for an existing category, a copy
                  for a template — and a plain add keeps the plus the single button would have shown. */}
              <ListItemIcon>{action.icon ?? <AddIcon fontSize="small" />}</ListItemIcon>
              <ListItemText
                primary={action.label}
                secondary={reason}
                slotProps={{ secondary: { id: reasonId } }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

/** One standalone action, drawn as an outlined button of its own. */
function StandaloneAction(props: Readonly<{ action: TableAddAction }>) {
  const { action } = props;
  const reasonId = useId();
  const disabled = action.disabled ?? false;
  const reason = disabled ? action.disabledReason : undefined;

  return (
    <Tooltip title={reason ?? ""} describeChild>
      <StandaloneActionWrapper>
        <LoadingButton
          variant="outlined"
          size="small"
          startIcon={action.icon}
          loading={action.loading ?? false}
          disabled={disabled}
          aria-describedby={reason ? reasonId : undefined}
          onClick={action.onClick}
        >
          {action.label}
        </LoadingButton>
        {reason && (
          <span id={reasonId} style={visuallyHidden}>
            {reason}
          </span>
        )}
      </StandaloneActionWrapper>
    </Tooltip>
  );
}
